import React from "react";

import { Grid } from "@material-ui/core";
import Collapse from "@material-ui/core/Collapse";
import { shallowEqual, useSelector } from "react-redux";

import { PaymentMethodChips } from "components/Chip";
import { OpenIndicator } from "components/Icons/CollapseIcon";
import { Column, Row } from "components/Layout";

import { BUYER_LABELS } from "constants/clearingSaleAttributes";

import {
  getConsignmentById,
  getCurrentSale,
  selectBidderNumbersByBuyerIdLookup,
  selectConsignmentIdsSaleLotIdsByBuyerIdLookup,
  getSaleLotIdsByBuyerId,
} from "selectors";

import { InvoiceNumber, DeploymentBusinessName } from "./BusinessComponents";
import { BuyerMenu } from "./CardOptionsMenu";
import { CommonLabels } from "./CommonLabels";
import { ExpandablePaper } from "./ExpandablePaper";
import { SaleLotSummary, BuyerLotStatus } from "./SaleLotSummary";
import { Title } from "./Title";
import { VendorSaleLotCards } from "./VendorSaleLotCards";

const BuyerBidderNumbers = ({ buyerId }) => {
  const isUsingRegisteredBidders = useSelector(
    state => getCurrentSale(state)?.using_registered_bidders,
  );
  // shallowEqual avoids re-rendering every buyer card when an unrelated sale
  // lot edit rebuilds the whole lookup with new (but equal) per-buyer arrays.
  const bidderNumbers = useSelector(
    state => selectBidderNumbersByBuyerIdLookup(state)[buyerId],
    shallowEqual,
  );

  if (!isUsingRegisteredBidders || !bidderNumbers?.length) {
    return null;
  }

  return <span> - {bidderNumbers.join(", ")}</span>;
};

export const BuyerCard = React.memo(({ id }) => {
  const saleLotIds = useSelector(getSaleLotIdsByBuyerId(id)) || [];

  const [expanded, setExpanded] = React.useState(false);
  const consignmentIds = Object.keys(
    useSelector(
      state => selectConsignmentIdsSaleLotIdsByBuyerIdLookup(state)[id],
    ),
  );

  const consignment = useSelector(getConsignmentById(consignmentIds[0]));

  return (
    <Column margin={2}>
      <ExpandablePaper
        expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <Row>
              <BuyerLotStatus buyerId={id} />

              <Title>
                <DeploymentBusinessName businessId={id} />
                <BuyerBidderNumbers buyerId={id} />
              </Title>
            </Row>
          </Grid>
          <Grid item xs={3}>
            <div className="flex flex-1 justify-end">
              <BuyerMenu businessId={id} />
            </div>
          </Grid>
          <Grid item xs={11}>
            <SaleLotSummary
              saleLotIds={saleLotIds}
              expanded={expanded}
              showPayments
            />
            <PaymentMethodChips buyerId={id} />
            <InvoiceNumber consignment={consignment} businessId={id} />
            <CommonLabels saleLotIds={saleLotIds} limitTo={BUYER_LABELS} />
          </Grid>

          <Grid item xs={1}>
            <OpenIndicator isOpen={expanded} />
          </Grid>
        </Grid>
      </ExpandablePaper>

      <Grid item xs={12}>
        <Collapse in={expanded} mountOnEnter unmountOnExit>
          {consignmentIds.map(consignmentId => {
            return (
              <VendorSaleLotCards
                key={consignmentId}
                consignmentId={consignmentId}
                buyerId={id}
              />
            );
          })}
        </Collapse>
      </Grid>
    </Column>
  );
});
